'use client';

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/** Keep the page frame stable across pagination, including shorter final pages.
 * Reset the frame for a different collection/filter or responsive width. */
export default function PaginatedContent({ children, loading = false, resetKey = '', label = 'Results', className = '' }: {
  children: ReactNode; loading?: boolean; resetKey?: string; label?: string; className?: string;
}) {
  const content = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const node = content.current;
    if (!node) return;
    let width = node.getBoundingClientRect().width;
    setHeight(node.getBoundingClientRect().height);
    const observer = new ResizeObserver(() => {
      const bounds = node.getBoundingClientRect();
      const resized = bounds.width !== width;
      width = bounds.width;
      setHeight(previous => resized ? bounds.height : Math.max(previous, bounds.height));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [resetKey]);
  return <div aria-label={label} aria-busy={loading} style={{ minHeight: height || undefined }}>
    <div role="status" className="sr-only">{loading ? 'Loading results…' : ''}</div>
    <div ref={content} inert={loading || undefined} className={`flow-root ${className}`}>{children}</div>
  </div>;
}
